import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import EntityAPI from '../services/entity';
import requestDeduplicator from '../services/requestDeduplicator';

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

export interface User {
    _id?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
}

interface SuperAdminContextType {
    superAdmins: User[];
    loading: boolean;
    error: string | null;
}

const SuperAdminContext = createContext<SuperAdminContextType>({
    superAdmins: [],
    loading: true,
    error: null
});

export const useSuperAdmins = () => useContext(SuperAdminContext);

interface SuperAdminProviderProps {
    children: ReactNode;
}

export const SuperAdminProvider: React.FC<SuperAdminProviderProps> = ({ children }) => {
    const [superAdmins, setSuperAdmins] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasFetched, setHasFetched] = useState(false);
    const [lastFetchTime, setLastFetchTime] = useState<number>(0);

    useEffect(() => {
        const fetchSuperAdmins = async () => {
            // Prevent multiple fetches
            if (hasFetched) {
                return;
            }

            // Check if user is authenticated before making API calls
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            if (!user.token) {
                setLoading(false);
                return;
            }

            // Check if user has permission to view admin users
            // Only Super Admins or users with users: true permission can access this
            if (user.user?.role !== 'Super Admin' && !user.user?.permissions?.users) {
                setLoading(false);
                setError(null);
                setHasFetched(true);
                return;
            }

            // Check if we have a recent cached result
            const now = Date.now();
            if (superAdmins.length > 0 && (now - lastFetchTime) < CACHE_DURATION) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                
                // Use deduplication to prevent multiple simultaneous calls
                const { data } = await requestDeduplicator.execute(
                    'super-admins',
                    () => EntityAPI.getEntityAdminMembershipUsers({ page: 1, limit: 100 })
                );
                
                // Filter only Super Admins
                const adminUsers = data.filter((user: User) => user.role === "Super Admin");
                setSuperAdmins(adminUsers);
                setError(null);
                setHasFetched(true);
                setLastFetchTime(now);
            } catch (err) {
                console.error("Error fetching Super Admins:", err);
                setError("Failed to load Super Admins");
            } finally {
                setLoading(false);
            }
        };

        fetchSuperAdmins();
    }, [hasFetched, superAdmins.length, lastFetchTime]);

    return (
        <SuperAdminContext.Provider value={{ superAdmins, loading, error }}>
            {children}
        </SuperAdminContext.Provider>
    );
};

export default SuperAdminProvider; 